import Decimal from 'decimal.js';
import { z } from 'zod';
import {
  ACTIVITY_TYPES,
  MOVEMENT_REASONS,
  ROLES,
  STOCK_LOCATIONS,
  TASK_PROGRESS_STEPS,
  UNITS,
  VAT_RATES,
  type TaskProgress,
  type VatRate,
} from '../constants';
import { isIsoDate } from '../dates';
import { parseDecimal } from '../money';

/** Structural twin of `Json` in packages/db/types.ts (kept local so shared stays dependency-free). */
export type JsonValue =
  string | number | boolean | null | { [key: string]: JsonValue | undefined } | JsonValue[];

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const PG_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d{1,6})?)?$/;
const TIMESTAMPTZ_RE =
  /^\d{4}-\d{2}-\d{2}[T ]([01]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d{1,9})?(Z|[+-]([01]\d|2[0-3])(:?[0-5]\d)?)$/;
const NUMERIC_STRING_RE = /^-?\d+(\.\d+)?$/;

function decimalPlaces(n: number): number {
  return new Decimal(n).decimalPlaces();
}

function isTimestamptz(s: string): boolean {
  if (!TIMESTAMPTZ_RE.test(s) || !isIsoDate(s.slice(0, 10))) return false;
  const normalized = s.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
  return !Number.isNaN(Date.parse(normalized));
}

export const uuid = z.string().uuid();

/** yyyy-MM-dd, validated as a real calendar date. */
export const isoDate = z.string().refine(isIsoDate);

/** HH:mm (form input). */
export const hhmm = z.string().regex(HHMM_RE);

/** Postgres `time` as returned by PostgREST: HH:mm or HH:mm:ss[.ffffff]. */
export const pgTime = z.string().regex(PG_TIME_RE);

/** ISO 8601 instant with offset or Z, as stored in `timestamptz` columns. */
export const timestamptz = z.string().refine(isTimestamptz);

/** Money amount: finite, ≥ 0, at most 2 decimals (numeric(12,2)). */
export const money = z
  .number()
  .finite()
  .nonnegative()
  .refine((n) => decimalPlaces(n) <= 2);

/** Money amount that may be negative (invoice reference totals parsed from OCR): finite, at most 2 decimals. */
export const signedMoney = z
  .number()
  .finite()
  .refine((n) => decimalPlaces(n) <= 2);

/** Unit price: finite, ≥ 0, at most 5 decimals (numeric(14,5), ARCHITECTURE.md D2). */
export const unitPrice = z
  .number()
  .finite()
  .nonnegative()
  .refine((n) => decimalPlaces(n) <= 5);

/** Quantity: finite, > 0, at most 3 decimals (numeric(12,3)). */
export const qty = z
  .number()
  .finite()
  .positive()
  .refine((n) => decimalPlaces(n) <= 3);

/** Quantity that may be zero: finite, ≥ 0, at most 3 decimals. */
export const qtyOrZero = z
  .number()
  .finite()
  .nonnegative()
  .refine((n) => decimalPlaces(n) <= 3);

export const unit = z.enum(UNITS);
export const role = z.enum(ROLES);
export const stockLocation = z.enum(STOCK_LOCATIONS);
export const movementReason = z.enum(MOVEMENT_REASONS);
export const activityType = z.enum(ACTIVITY_TYPES);

export const vatRate = z.number().refine((v): v is VatRate => (VAT_RATES as readonly number[]).includes(v));

export const taskProgress = z
  .number()
  .refine((v): v is TaskProgress => (TASK_PROGRESS_STEPS as readonly number[]).includes(v));

/** Trimmed string with at least one character. */
export const nonEmptyTrimmed = z.string().trim().min(1);

/** Same as `nonEmptyTrimmed` with the prototype's toast text as the error message. */
export function requiredText(message: string) {
  return z.string().trim().min(1, { message });
}

/**
 * Postgres `numeric` columns: PostgREST sends JSON numbers, but supabase-js may deliver strings in
 * some configurations. Accept both; anything else (null, boolean, '') is rejected, never coerced.
 */
export const dbNumeric = z.preprocess(
  (v) => (typeof v === 'string' && NUMERIC_STRING_RE.test(v.trim()) ? Number(v) : v),
  z.number().finite(),
);

export const dbMoney = dbNumeric.pipe(money);
export const dbSignedMoney = dbNumeric.pipe(signedMoney);
export const dbUnitPrice = dbNumeric.pipe(unitPrice);
export const dbQty = dbNumeric.pipe(qty);
export const dbQtyOrZero = dbNumeric.pipe(qtyOrZero);

/** Form input for quantities/prices: `,` or `.` decimals via `parseDecimal`; rejects with `message`. */
export function decimalInputWith(message: string) {
  return z.union([z.string(), z.number()]).transform((raw, ctx) => {
    const n = parseDecimal(raw);
    if (n === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      return z.NEVER;
    }
    return n;
  });
}

/** Default decimal input — prototype transferStock toast (line 2250). */
export const decimalInput = decimalInputWith('Shkruaj një sasi valide');

/** Recursive JSON value (jsonb columns). */
export const jsonValue: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValue),
    z.record(jsonValue.optional()),
  ]),
);

/** The message the UI should toast for a failed parse: the first issue, in schema key order. */
export function firstIssueMessage(error: z.ZodError): string {
  return error.issues[0]?.message ?? '';
}
